/**
 * Helper function to create a hash URL without double slashes
 * @param path The path to convert to a hash URL
 * @returns A properly formatted hash URL
 */
function createHashUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `#/${cleanPath}`;
}

/**
 * Builds a proper redirect URL with the account base path
 * @param returnTo The return path (e.g., '/game-assist', '/settings?tab=teams')
 * @returns Object with fullUrl and hash properties for different redirect scenarios
 */
export function buildRedirectUrl(returnTo: string): { fullUrl: string; hash: string } {
  // Ensure returnTo starts with /
  const cleanReturnTo = returnTo.startsWith('/') ? returnTo : `/${returnTo}`;
  
  // If it's an external URL (starts with http), return as-is
  if (cleanReturnTo.startsWith('http')) {
    return {
      fullUrl: cleanReturnTo,
      hash: cleanReturnTo
    };
  }
  
  // For internal paths
  const baseUrl = window.location.origin;
  return {
    fullUrl: `${baseUrl}${cleanReturnTo}`,
    hash: cleanReturnTo
  };
}


/**
 * Generates a UUID with fallback mechanisms for different browser support levels
 * @returns {string} A UUID v4 string
 */
export const generateUUID = () => {
  // Check if crypto.randomUUID is available
  if (crypto && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback implementation using crypto.getRandomValues
  if (crypto && typeof crypto.getRandomValues === 'function') {
    return (1e7 + -1e3 + -4e3 + -8e3 + -1e11).toString().replace(/[018]/g, (c) =>
      (
        parseInt(c) ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (parseInt(c) / 4)))
      ).toString(16)
    );
  }

  // Last resort fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Parses URL parameters from hash-based routing
 * @returns {URLSearchParams} Parsed URL parameters from the hash with cleaned values
 */
export function getHashBasedUrlParams(): URLSearchParams {
  // Parse parameters from hash-based routing
  const hash = window.location.hash;
  let urlParams = new URLSearchParams();
  
  // Extract query string from hash (e.g., #/signup-complete?token=value&app=signal)
  const hashParts = hash.split('?');
  if (hashParts.length > 1) {
    // Join all parts after the first '?' in case there are multiple '?' in the URL
    const queryString = hashParts.slice(1).join('?');
    urlParams = new URLSearchParams(queryString);
  }
  
  // Create a new URLSearchParams with cleaned values
  const cleanedParams = new URLSearchParams();
  for (const [key, value] of urlParams.entries()) {
    // Clean the value by trimming whitespace and removing extra quotes/commas
    const cleanedValue = value.trim().replace(/^["']|["'],?$/g, '');
    cleanedParams.set(key, cleanedValue);
  }
  
  return cleanedParams;
}

/**
 * Check if user has seen an onboarding step (auto-marks as seen on server if not)
 * @param {string} onboardingStep - The onboarding step to check
 * @returns {Promise<boolean>} Returns false if step was newly seen (should show popup), true if already seen
 */
export async function checkOnboardingStep(onboardingStep: string): Promise<boolean> {
  const { checkOnboardingPopup } = await import('./apiService');
  
  return new Promise((resolve) => {
    checkOnboardingPopup(onboardingStep, (result, status) => {
      if (status === 200) {
        // Return the inverse of has_seen
        // false = should show popup (was newly seen)
        // true = don't show popup (already seen)
        resolve(result.has_seen);
      } else {
        // API error, assume not seen to be safe (show popup)
        console.error(`Failed to check onboarding step '${onboardingStep}', status: ${status}`);
        resolve(false);
      }
    });
  });
}

// Hash function for privacy
export async function hashPhoneNumber(phoneNumber: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(phoneNumber);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}